import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { FiLogOut } from "react-icons/fi";
import Image from "next/image";
import styles from "@/styles/HeroImageSection.module.css";
import RoleSelectionModal from "@/components/Modal/loginModal";
import LogoutModal from "@/components/Modal/LogoutModal";
import style from "./Header.module.css";

function HeaderBanner() {
  const [scrolled, setScrolled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [user, setUser] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);

    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) {
        setDropdownOpen(false);
      }

      checkHeaderTopBarHeight(mobile);
    };

    const checkHeaderTopBarHeight = (mobile = false) => {
      if (!mobile) {
        const headerTopBar = document.querySelector(".rbt-header-top");
        if (headerTopBar) {
          const isVisible =
            window.getComputedStyle(headerTopBar).display !== "none";
          setTopBarHeight(isVisible ? headerTopBar.offsetHeight : 0);
        } else {
          setTopBarHeight(0);
        }
      } else {
        setTopBarHeight(0);
      }
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && !isMobile) {
          const headerTopBar = document.querySelector(".rbt-header-top");
          if (!headerTopBar) {
            setTopBarHeight(0);
          } else {
            checkHeaderTopBarHeight();
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    handleResize();
    checkHeaderTopBarHeight();

    const intervalId = setInterval(() => {
      if (!isMobile) {
        checkHeaderTopBarHeight();
      }
    }, 500);

    window.addEventListener("resize", handleResize);

    const handleClickOutside = (event) => {
      if (
        dropdownOpen &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("mousedown", handleClickOutside);
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, [dropdownOpen, isMobile]);

  const handleLogout = async () => {
    console.log("Reached handleLogout");
    try {
      await fetch(`${process.env.NEXT_PUBLIC_API_URL}/logout`, {
        method: "GET",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }

    localStorage.removeItem("user");
    setUser(null);
    setShowConfirm(false);
  };

  const handleLoginOrLogoutClick = (e) => {
    e.preventDefault();

    if (user) {
      setShowConfirm(true);
    } else {
      setShowLoginModal(true);
    }
  };

  const handleCancelLogout = () => {
    setShowConfirm(false);
  };

  return (
    <>
      <RoleSelectionModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        type="login"
      />

      {showConfirm && (
        <LogoutModal
          handleLogout={handleLogout}
          handleCancelLogout={handleCancelLogout}
        />
      )}

      <style jsx>{`
        /* Header Styles */
        .header-container {
          width: 100%;
          position: fixed;
          top: ${topBarHeight > 0 ? topBarHeight - 0 : 0}px;
          left: 0;
          z-index: 998;
          transition: background-color 0.3s ease, top 0.3s ease;
          background-color: ${scrolled ? "rgba(0,0,0,0.8)" : "transparent"};
          backdrop-filter: ${scrolled ? "blur(10px)" : "none"};
          box-shadow: ${scrolled ? "0 2px 8px rgba(0, 0, 0, 0.15)" : "none"};
          height: 80px;
        }

        .header-inner {
          height: 100%;
          display: flex;
          align-items: center;
        }

        /* Logo Container */
        .logo-container {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
        }

        /* Logo Styles */
        .header-logo-mobile {
          width: 100px;
          height: 100px;
          object-fit: contain;
        }

        .header-logo-desktop {
          width: 140px;
          height: 140px;
          object-fit: contain;
        }

        /* Button Styles */
        .btn-glass {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: white;
          padding: 8px 20px;
          border-radius: 10px;
          font-weight: 500;
          font-size: 14px;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .btn-glass:hover {
          background: rgba(255, 255, 255, 0.2);
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
        }

        .btn-gradient {
          background: linear-gradient(90deg, #a259e0, #6366f1);
          color: white;
          border: none;
          padding: 8px 24px;
          border-radius: 10px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 15px rgba(162, 89, 224, 0.3);
        }

        .btn-gradient:hover {
          background: linear-gradient(90deg, #8b4bc5, #4f52d6);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(162, 89, 224, 0.5);
        }

        /* Media Queries */
        @media (max-width: 575.98px) {
          .header-container {
            height: 70px;
            top: 0 !important;
          }

          .btn-glass, .btn-gradient {
            padding: 6px 14px;
            font-size: 12px;
          }

          .header-logo-mobile {
            width: 85px;
            height: 85px;
          }
        }

        @media (min-width: 576px) and (max-width: 767.98px) {
          .header-container {
            height: 75px;
            top: 0 !important;
          }

          .btn-glass, .btn-gradient {
            padding: 8px 16px;
            font-size: 13px;
          }

          .header-logo-mobile {
            width: 90px;
            height: 90px;
          }
        }

        @media (min-width: 768px) and (max-width: 991.98px) {
          .header-container {
            height: 80px;
          }

          .header-logo-desktop {
            width: 120px;
            height: 120px;
          }
        }

        @media (min-width: 992px) and (max-width: 1199.98px) {
          .header-container {
            height: 85px;
          }

          .header-logo-desktop {
            width: 130px;
            height: 130px;
          }
        }

        @media (min-width: 1200px) {
          .header-container {
            height: 90px;
          }

          .header-logo-desktop {
            width: 140px;
            height: 140px;
          }
        }

        @media (min-width: 1400px) {
          .header-container {
            height: 95px;
          }

          .header-logo-desktop {
            width: 150px;
            height: 150px;
          }
        }

        @media (max-width: 767px) and (orientation: landscape) {
          .header-container {
            height: 65px;
            top: 0 !important;
          }

          .btn-glass, .btn-gradient {
            padding: 6px 12px;
            font-size: 12px;
          }

          .header-logo-mobile {
            width: 75px;
            height: 75px;
          }
        }
      `}</style>

      <header className="header-container">
        <div className="container-fluid header-inner">
          <div className="row w-100 justify-content-between align-items-center flex-nowrap">
            <div className="col-auto d-flex align-items-center flex-shrink-0">
              <div className="logo-container">
                <Image
                  src="/images/logo/elms-logo-white2.png"
                  width={100}
                  height={100}
                  alt="Company Logo"
                  priority
                  className="header-logo-mobile d-block d-md-none"
                />
                <Image
                  src="/images/logo/elms-logo-white2.png"
                  width={140}
                  height={140}
                  alt="Company Logo"
                  priority
                  className="header-logo-desktop d-none d-md-block"
                />
              </div>
            </div>

            <div className="col-auto ms-auto">
              <div
                className="d-flex flex-column flex-md-row align-items-center gap-3 position-relative"
                ref={dropdownRef}
              >
                <div className="d-flex align-items-center gap-2">
                  <div className="d-flex gap-3 align-items-center">
                    <button
                      onClick={handleLoginOrLogoutClick}
                      className="btn-gradient"
                    >
                      {user ? (
                        <>
                          Logout <FiLogOut className="ms-2" />
                        </>
                      ) : (
                        "Login"
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

export default HeaderBanner;